import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faEye, 
  faCheckCircle, 
  faTimesCircle, 
  faChartLine, 
  faMobile, 
  faDesktop,
  faArrowUp,
  faArrowDown,
  faClock,
  faMousePointer
} from '@fortawesome/free-solid-svg-icons';
import AdminSalesPopupStatisticsService from '../../services/AdminSalesPopupStatisticsService';
import '../../styles/settings/SalesPopupStatistics.css';

const SalesPopupStatistics = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dateRange, setDateRange] = useState(30);

  useEffect(() => {
    loadStatistics();
  }, [dateRange]);

  const loadStatistics = async () => {
    try {
      setLoading(true);
      setError(null);
      const stats = await AdminSalesPopupStatisticsService.getStatistics(dateRange);
      setStatistics(stats);
    } catch (err) {
      console.error('Error loading statistics:', err);
      setError('Error loading statistics. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0';
    return new Intl.NumberFormat('en-US').format(num);
  };

  const formatPercentage = (num) => {
    if (num === null || num === undefined || isNaN(num)) return '0.00%';
    return `${Number(num).toFixed(2)}%`;
  };

  const getChangeColor = (change) => {
    if (change > 0) return 'positive';
    if (change < 0) return 'negative';
    return 'neutral';
  };

  const getChangeIcon = (change) => {
    if (change > 0) return faArrowUp;
    if (change < 0) return faArrowDown;
    return null;
  };

  if (loading) {
    return (
      <div className="sales-popup-statistics">
        <div className="statistics-loading">
          <div className="loading-spinner"></div>
          <p>Loading statistics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sales-popup-statistics">
        <div className="statistics-error">
          <p>{error}</p>
          <button className="button" onClick={loadStatistics}>Retry</button>
        </div>
      </div>
    );
  }

  if (!statistics) {
    return (
      <div className="sales-popup-statistics">
        <div className="statistics-empty">
          <p>No statistics available yet. Start tracking events to see analytics.</p>
        </div>
      </div>
    );
  }

  const { overview, rates, engagement, trends, sources, comparison } = statistics;

  return (
    <div className="sales-popup-statistics">
      <div className="statistics-header">
        <div className="date-range-selector">
          <label htmlFor="date-range">Time Range:</label>
          <select
            id="date-range"
            value={dateRange} 
            onChange={(e) => setDateRange(Number(e.target.value))}
            className="date-range-select"
          >
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="statistics-metrics">
        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faEye} className="metric-icon" />
            <span className="metric-label">Impressions</span>
          </div>
          <div className="metric-value">{formatNumber(overview.total_impressions)}</div>
          {comparison.impressions_change !== 0 && (
            <div className={`metric-change ${getChangeColor(comparison.impressions_change)}`}>
              <FontAwesomeIcon icon={getChangeIcon(comparison.impressions_change)} />
              <span>{Math.abs(comparison.impressions_change).toFixed(1)}%</span>
            </div>
          )}
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faCheckCircle} className="metric-icon" />
            <span className="metric-label">Conversions</span>
          </div>
          <div className="metric-value">{formatNumber(overview.total_conversions)}</div>
          <div className="metric-subtext">
            {formatPercentage(rates.conversion_rate)} conversion rate
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faTimesCircle} className="metric-icon" />
            <span className="metric-label">Closes</span>
          </div>
          <div className="metric-value">{formatNumber(overview.total_closes)}</div>
          <div className="metric-subtext">
            {formatPercentage(rates.close_rate)} close rate
          </div>
        </div>

        <div className="metric-card">
          <div className="metric-header">
            <FontAwesomeIcon icon={faMousePointer} className="metric-icon" />
            <span className="metric-label">Button Clicks</span>
          </div>
          <div className="metric-value">{formatNumber(overview.total_button_clicks)}</div>
          <div className="metric-subtext">
            {formatPercentage(rates.click_through_rate)} CTR
          </div>
        </div>
      </div>

      {/* Conversion Rate Comparison */}
      <div className="statistics-section">
        <h3>Conversion Rate</h3>
        <div className="conversion-rate-card">
          <div className="conversion-rate-main">
            <span className="conversion-rate-value">{formatPercentage(rates.conversion_rate)}</span>
            {comparison.conversion_rate_change !== 0 && (
              <div className={`conversion-rate-change ${getChangeColor(comparison.conversion_rate_change)}`}>
                <FontAwesomeIcon icon={getChangeIcon(comparison.conversion_rate_change)} />
                <span>{Math.abs(comparison.conversion_rate_change).toFixed(2)}%</span>
                <span className="change-label">vs previous period</span>
              </div>
            )}
          </div>
          <div className="conversion-rate-comparison">
            <span>Previous: {formatPercentage(comparison.prev_conversion_rate)}</span>
          </div>
        </div>
      </div>

      {/* Engagement Metrics */}
      <div className="statistics-section">
        <h3>Engagement</h3>
        <div className="engagement-metrics">
          <div className="engagement-card">
            <FontAwesomeIcon icon={faClock} className="engagement-icon" />
          <div className="engagement-value">{engagement?.avg_time_on_page ? engagement.avg_time_on_page.toFixed(1) : '0.0'}s</div>
          <div className="engagement-label">Avg. Time on Page</div>
          </div>
          <div className="engagement-card">
            <FontAwesomeIcon icon={faChartLine} className="engagement-icon" />
            <div className="engagement-value">{engagement?.avg_scroll_depth ? engagement.avg_scroll_depth.toFixed(0) : '0'}%</div>
            <div className="engagement-label">Avg. Scroll Depth</div>
          </div>
          <div className="engagement-card">
            <FontAwesomeIcon icon={faEye} className="engagement-icon" />
            <div className="engagement-value">{formatNumber(overview.unique_sessions)}</div>
            <div className="engagement-label">Unique Sessions</div>
          </div>
        </div>
      </div>

      {/* Device Breakdown */}
      <div className="statistics-section">
        <h3>Device Breakdown</h3>
        <div className="device-breakdown">
          <div className="device-card">
            <FontAwesomeIcon icon={faDesktop} className="device-icon" />
          <div className="device-value">{formatNumber(sources?.mobile_vs_desktop?.desktop || 0)}</div>
          <div className="device-label">Desktop</div>
          </div>
          <div className="device-card">
            <FontAwesomeIcon icon={faMobile} className="device-icon" />
            <div className="device-value">{formatNumber(sources?.mobile_vs_desktop?.mobile || 0)}</div>
            <div className="device-label">Mobile</div>
          </div>
        </div>
      </div>

      {/* Daily Trends */}
      {trends?.daily_data && Object.keys(trends.daily_data).length > 0 && (
        <div className="statistics-section">
          <h3>Daily Trends</h3>
          <div className="daily-trends">
            {(() => {
              const dailyEntries = Object.entries(trends.daily_data).slice(-14); // Last 14 days
              const maxImpressions = Math.max(...dailyEntries.map(([_, d]) => d.impressions || 0), 1);
              const maxConversions = Math.max(...dailyEntries.map(([_, d]) => d.conversions || 0), 1);
              
              return dailyEntries.map(([date, data]) => (
                <div key={date} className="daily-trend-item">
                  <div className="trend-date">{new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</div>
                  <div className="trend-bars">
                    <div 
                      className="trend-bar impressions" 
                      style={{ width: `${((data.impressions || 0) / maxImpressions) * 100}%` }}
                    ></div>
                    <div 
                      className="trend-bar conversions" 
                      style={{ width: `${((data.conversions || 0) / maxConversions) * 100}%` }}
                    ></div>
                  </div>
                  <div className="trend-values">
                    <span className="trend-value">{data.impressions || 0} impressions</span>
                    <span className="trend-value">{data.conversions || 0} conversions</span>
                  </div>
                </div>
              ));
            })()}
          </div>
        </div>
      )}
    </div>
  );
};

export default SalesPopupStatistics;

